import type {
    Course,
    Testimonial,
    PricingPlan,
    Step,
    Benefit,
    NavigationItem,
    SocialLink,
    ContactInfo,
    HeroContent,
    SEOData
} from '../types';

export const heroContent: HeroContent = {
    title: "SwarSadhana",
    tagline: "Harmony in Every Note and Pose",
    subtitle: "Personalized 1-on-1 live classes via subscription - Learn at your pace with global access and Indian soul",
    primaryCTA: "Start Free Trial",
    secondaryCTA: "Explore Courses",
    backgroundImage: "/images/hero-background.jpg"
};

export const courses: Course[] = [
    {
        id: "music",
        title: "Music",
        description: "Master tabla with experts, explore Hindustani vocals, and discover the soul of Indian classical music through personalized instruction.",
        category: "music",
        icon: "/images/music-classes.svg",
        ageRange: "4-75+",
        price: 12000,
        currency: "₹",
        classCount: 12,
        level: "beginner",
        topics: ["Svara Practice", "Tabla Basics", "Hindustani Vocals", "Rhythm Patterns"],
        instructor: "Pandit Ravi Kumar",
        duration: "45 minutes",
        features: [
            "Hindustani & Carnatic Vocals",
            "Tabla & Percussion Mastery",
            "Sitar & Traditional Instruments",
            "Flexible scheduling",
            "User dashboard with progress tracking",
            "Live interactive sessions"
        ]
    },
    {
        id: "yoga",
        title: "Yoga",
        description: "Discover inner peace through Hatha yoga and traditional practices, guided by expert instructors at your own pace.",
        category: "yoga",
        icon: "/images/yoga-classes.svg",
        ageRange: "8-80+",
        price: 10000,
        currency: "₹",
        classCount: 12,
        level: "beginner",
        topics: ["Asana Practice", "Pranayama", "Meditation", "Chakra Alignment"],
        instructor: "Guru Priya Sharma",
        duration: "60 minutes",
        features: [
            "Hatha Yoga Fundamentals",
            "Vinyasa Flow Sessions",
            "Meditation & Pranayama",
            "Personalized flexibility training",
            "Progress tracking dashboard",
            "Learn at your pace"
        ]
    },
    {
        id: "dance",
        title: "Dance",
        description: "Express yourself through Bharatanatyam classical dance or energetic Bollywood choreography with personalized instruction.",
        category: "dance",
        icon: "/images/dance-classes.svg",
        ageRange: "5-65+",
        price: 15000,
        currency: "₹",
        classCount: 12,
        level: "beginner",
        topics: ["Basic Adavus", "Mudras", "Bollywood Choreography", "Expression Training"],
        instructor: "Natya Guru Meera Devi",
        duration: "50 minutes",
        isPopular: true,
        features: [
            "Bharatanatyam Classical",
            "Bollywood Dance Styles",
            "Contemporary Fusion",
            "Custom choreography sessions",
            "Performance preparation",
            "Global access, Indian soul"
        ]
    }
];

export const testimonials: Testimonial[] = [
    {
        id: "1",
        name: "Arjun",
        age: 12,
        course: "Tabla Student",
        quote: "Loved my vocal classes! My teacher makes learning tabla so fun and easy. I can now play basic rhythms!",
        image: "/images/testimonial-1.webp",
        rating: 5,
        location: "Mumbai, India"
    },
    {
        id: "2",
        name: "Sarah",
        age: 35,
        course: "Yoga Practitioner",
        quote: "The personalized yoga sessions have transformed my daily routine. Perfect balance of tradition and modern convenience.",
        image: "/images/testimonial-2.webp",
        rating: 5,
        location: "New York, USA"
    },
    {
        id: "3",
        name: "Lakshmi",
        age: 28,
        course: "Bharatanatyam Dancer",
        quote: "Learning Bharatanatyam online seemed impossible, but SwarSadhana made it feel like my guru was right there with me!",
        image: "/images/testimonial-3.webp",
        rating: 5,
        location: "Chennai, India"
    }
];

export const pricingPlans: PricingPlan[] = [
    {
        id: "basic",
        name: "Basic",
        price: 1000,
        currency: "₹",
        period: "month",
        classes: 4,
        description: "Perfect for beginners exploring their interests",
        stripePriceId: "price_basic_monthly",
        features: [
            "4 one-on-one live classes per month",
            "Basic progress tracking",
            "Email support",
            "Flexible scheduling",
            "Mobile app access",
            "Learning materials access"
        ]
    },
    {
        id: "standard",
        name: "Standard",
        price: 2000,
        currency: "₹",
        period: "month",
        classes: 8,
        description: "Ideal for dedicated learners",
        popular: true,
        stripePriceId: "price_standard_monthly",
        features: [
            "8 one-on-one live classes per month",
            "Advanced progress tracking",
            "Priority support",
            "Unlimited scheduling changes",
            "Access to exclusive workshops",
            "Performance opportunities",
            "Personalized learning path"
        ]
    },
    {
        id: "premium",
        name: "Premium",
        price: 3000,
        currency: "₹",
        period: "month",
        classes: 12,
        description: "For serious practitioners and performers",
        stripePriceId: "price_premium_monthly",
        features: [
            "12 one-on-one live classes per month",
            "Premium dashboard with analytics",
            "24/7 priority support",
            "Unlimited scheduling flexibility",
            "Exclusive masterclasses",
            "Performance coaching",
            "Recording session access",
            "One-on-one mentorship"
        ]
    }
];

export const steps: Step[] = [
    {
        id: "1",
        number: 1,
        title: "Choose Your Path",
        description: "Select your preferred subscription plan and create your personalized learning account with us.",
        icon: "/images/step-1.webp"
    },
    {
        id: "2",
        number: 2,
        title: "Schedule Your Class",
        description: "Book convenient time slots with our certified instructors through your user dashboard.",
        icon: "/images/step-2.webp"
    },
    {
        id: "3",
        number: 3,
        title: "Learn & Grow",
        description: "Begin your transformative learning journey with expert guidance tailored to your pace and goals.",
        icon: "/images/step-3.webp"
    }
];

export const benefits: Benefit[] = [
    {
        id: "1",
        title: "Certified Expert Instructors",
        description: "Learn from masters with years of experience in traditional and contemporary Indian arts",
        icon: "GraduationCap"
    },
    {
        id: "2",
        title: "Personalized 1-on-1 Classes",
        description: "Every session is tailored to your unique learning style and pace with flexible scheduling",
        icon: "Heart"
    },
    {
        id: "3",
        title: "For All Ages (4-75+)",
        description: "From curious kids to passionate seniors, everyone finds their rhythm and harmony here",
        icon: "Users"
    },
    {
        id: "4",
        title: "User Dashboard",
        description: "Track your progress, schedule classes, and access learning materials through your personal dashboard",
        icon: "BarChart3"
    },
    {
        id: "5",
        title: "Global Access, Indian Soul",
        description: "Connect with authentic Indian culture from anywhere in the world through live online classes",
        icon: "Globe"
    },
    {
        id: "6",
        title: "Progress Tracking",
        description: "Monitor your growth with personalized feedback, milestones, and performance analytics",
        icon: "Award"
    }
];

export const navigation: NavigationItem[] = [
    { id: "home", label: "Home", href: "#home" },
    { id: "courses", label: "Courses", href: "#courses" },
    { id: "pricing", label: "Pricing", href: "#pricing" },
    { id: "about", label: "About", href: "#about" },
    { id: "contact", label: "Contact", href: "#contact" },
    { id: "login", label: "Login", href: "/login", external: true }
];

export const socialLinks: SocialLink[] = [
    { id: "instagram", platform: "Instagram", url: "https://instagram.com/swarsadhana", icon: "Instagram" },
    { id: "youtube", platform: "YouTube", url: "https://youtube.com/swarsadhana", icon: "Youtube" },
    { id: "facebook", platform: "Facebook", url: "https://facebook.com/swarsadhana", icon: "Facebook" },
    { id: "twitter", platform: "Twitter", url: "https://twitter.com/swarsadhana", icon: "Twitter" }
];

export const contactInfo: ContactInfo = {
    email: "support@swarsadhana.com",
    phone: "+91-XXX-XXX-XXXX",
    hours: "Mon-Sun: 6 AM - 10 PM IST",
    address: "Mumbai, Maharashtra, India"
};

export const seoData: SEOData = {
    title: "SwarSadhana - Live Music, Yoga & Dance Classes | Subscription-Based Learning",
    description: "Join SwarSadhana for personalized 1-on-1 live classes in Indian music, yoga, and dance. Learn tabla, Hindustani vocals, Hatha yoga, and Bharatanatyam from expert instructors. Flexible scheduling, global access.",
    keywords: [
        "SaaS music classes",
        "live yoga classes",
        "Bollywood dance lessons",
        "Hindustani vocals online",
        "tabla lessons",
        "Bharatanatyam classes",
        "Hatha yoga online",
        "Indian classical music",
        "online dance classes",
        "subscription learning platform",
        "1-on-1 music lessons",
        "personalized yoga instruction"
    ],
    ogImage: "/images/og-image.webp",
    canonicalUrl: "https://swarsadhana.com",
    structuredData: {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "name": "SwarSadhana",
        "description": "Personalized 1-on-1 live classes in Indian music, yoga, and dance",
        "url": "https://swarsadhana.com",
        "logo": "https://swarsadhana.com/images/logo.webp",
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+91-XXX-XXX-XXXX",
            "contactType": "customer service",
            "email": "support@swarsadhana.com"
        },
        "address": {
            "@type": "PostalAddress",
            "addressCountry": "IN",
            "addressRegion": "Maharashtra",
            "addressLocality": "Mumbai"
        },
        "offers": [
            {
                "@type": "Offer",
                "name": "Basic Plan",
                "price": "1000",
                "priceCurrency": "INR",
                "description": "4 one-on-one live classes per month"
            },
            {
                "@type": "Offer",
                "name": "Standard Plan",
                "price": "2000",
                "priceCurrency": "INR",
                "description": "8 one-on-one live classes per month"
            },
            {
                "@type": "Offer",
                "name": "Premium Plan",
                "price": "3000",
                "priceCurrency": "INR",
                "description": "12 one-on-one live classes per month"
            }
        ]
    }
};

// API Configuration - Optional for landing page
export const API_BASE_URL = '';

export const API_ENDPOINTS = {
    courses: '/courses',
    courseById: (id: string) => `/courses/${id}`,
    trialSignup: '/trial-signup',
    newsletter: '/newsletter',
    contact: '/contact',
    auth: {
        login: '/auth/login',
        register: '/auth/register',
        logout: '/auth/logout',
        profile: '/auth/profile'
    },
    subscription: {
        plans: '/subscription/plans',
        subscribe: '/subscription/subscribe',
        cancel: '/subscription/cancel',
        update: '/subscription/update'
    }
};

// Theme Configuration
export const theme = {
    colors: {
        primary: {
            magenta: '#C71585',
            green: '#2E8B57',
            gold: '#FFD700'
        },
        secondary: {
            ivory: '#FFFFF0',
            white: '#FFFFFF'
        },
        text: {
            primary: '#1F2937',
            secondary: '#6B7280',
            muted: '#9CA3AF'
        },
        background: {
            primary: '#FFFFFF',
            secondary: '#F9FAFB',
            accent: '#FFF8DC'
        }
    },
    fonts: {
        heading: 'Cinzel, serif',
        body: 'Poppins, sans-serif'
    },
    spacing: {
        xs: '0.5rem',
        sm: '1rem',
        md: '1.5rem',
        lg: '2rem',
        xl: '3rem'
    },
    breakpoints: {
        mobile: '768px',
        tablet: '1024px',
        desktop: '1280px'
    }
};

// Animation Variants
export const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -30 }
};

export const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 50 }
};

export const slideInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 }
};

export const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 }
};

export const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2
        }
    }
};

// Advanced Animation Variants
export const bounceIn = {
    hidden: { opacity: 0, scale: 0.3 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            type: "spring",
            stiffness: 400,
            damping: 10
        }
    }
};

export const rotateIn = {
    hidden: { opacity: 0, rotate: -180, scale: 0.5 },
    visible: {
        opacity: 1,
        rotate: 0,
        scale: 1,
        transition: {
            type: "spring",
            stiffness: 200,
            damping: 15
        }
    }
};

export const flipIn = {
    hidden: { opacity: 0, rotateY: -90 },
    visible: {
        opacity: 1,
        rotateY: 0,
        transition: {
            duration: 0.6,
            ease: "easeOut"
        }
    }
};

export const slideInFromBottom = {
    hidden: { opacity: 0, y: 100 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 100,
            damping: 15
        }
    }
};

export const fadeInScale = {
    hidden: { opacity: 0, scale: 0.8, y: 20 },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            duration: 0.5,
            ease: "easeOut"
        }
    }
};

export const staggerFast = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.05,
            delayChildren: 0.1
        }
    }
};

export const staggerSlow = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
            delayChildren: 0.3
        }
    }
};

export const pulseAnimation = {
    scale: [1, 1.05, 1],
    transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
    }
};

export const floatingAnimation = {
    y: [0, -10, 0],
    transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
    }
};

export const rotatingAnimation = {
    rotate: [0, 360],
    transition: {
        duration: 20,
        repeat: Infinity,
        ease: "linear"
    }
};

export const swayAnimation = {
    x: [0, 10, 0, -10, 0],
    transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
    }
};

// Scroll-triggered animations
export const scrollFadeIn = {
    hidden: { opacity: 0, y: 50 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.6,
            ease: "easeOut"
        }
    }
};

export const scrollSlideIn = {
    hidden: { opacity: 0, x: -100 },
    visible: {
        opacity: 1,
        x: 0,
        transition: {
            duration: 0.8,
            ease: "easeOut"
        }
    }
};

export const scrollScaleIn = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            duration: 0.7,
            ease: "easeOut"
        }
    }
};

// Card hover animations
export const cardHover = {
    rest: { scale: 1, y: 0 },
    hover: {
        scale: 1.03,
        y: -5,
        transition: {
            duration: 0.3,
            ease: "easeOut"
        }
    }
};

export const cardTilt = {
    rest: { rotateX: 0, rotateY: 0 },
    hover: {
        rotateX: 5,
        rotateY: 5,
        transition: {
            duration: 0.3,
            ease: "easeOut"
        }
    }
};

// Button animations
export const buttonPulse = {
    scale: [1, 1.05, 1],
    transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut"
    }
};

export const buttonBounce = {
    y: [0, -3, 0],
    transition: {
        duration: 0.6,
        repeat: Infinity,
        ease: "easeInOut"
    }
};

// Complex stagger animations
export const staggerCards = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.15,
            delayChildren: 0.2,
            when: "beforeChildren"
        }
    }
};

export const staggerList = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08,
            delayChildren: 0.1
        }
    }
};

// Parallax and scroll effects
export const parallaxSlow = {
    y: [0, -50],
    transition: {
        duration: 1,
        ease: "linear"
    }
};

export const parallaxFast = {
    y: [0, -100],
    transition: {
        duration: 1,
        ease: "linear"
    }
};

// Loading animations
export const spinnerAnimation = {
    rotate: [0, 360],
    transition: {
        duration: 1,
        repeat: Infinity,
        ease: "linear"
    }
};

export const dotsAnimation = {
    scale: [1, 1.2, 1],
    opacity: [0.5, 1, 0.5],
    transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut"
    }
}; 
